/** Assign a distance to its local endpoint, never to a paragraph's topic. */
export type DistanceRole = 'substation' | 'reactor' | 'search-radius' | 'other' | 'unknown';

export function classifyDistanceMention(text: string, index: number, length: number): DistanceRole {
  const beforeSentence = Math.max(text.lastIndexOf('. ', index), text.lastIndexOf('? ', index), text.lastIndexOf('! ', index), text.lastIndexOf(';', index), text.lastIndexOf('|', index));
  const start = Math.max(0, beforeSentence + 1, index - 180);
  const ends = ['. ', '? ', '! ', ';', '|'].map(stop => text.indexOf(stop, index + length)).filter(at => at >= 0);
  const end = Math.min(text.length, index + length + 180, ...(ends.length ? ends : [text.length]));
  const before = text.slice(start, index);
  const after = text.slice(index + length, end);
  if (/\b(?:within|radius(?:\s+of)?|search\s+radius(?:\s+of)?|buffer(?:\s+of)?)\s*$/i.test(before)
    || /^\s*(?:[- ]?(?:search|query|buffer)\s+)?radius\b/i.test(after)) return 'search-radius';
  if (/^\s*(?:by\s+road|road\s+distance|driving\s+distance|route\s+distance)\b/i.test(after)) return 'other';
  // A distance that directly quantifies the length of new/proposed transmission line
  // segments (e.g. "a 5-mile 345 kV line", "5-mile and 14-mile 345 kV line additions",
  // "19 miles total (5 miles plus 14 miles) of new 345 kV transmission lines") is a
  // line-build length, not a site-to-substation proximity distance.
  if (
    /^\s*(?:total\s*(?:\([^)]*\)\s*)?)?(?:of\s+)?(?:and\s+[\d,]+(?:\.\d+)?[-\s]*(?:km|mi|miles?|kilometers?)\s+)?(?:(?:new|proposed|modeled|overhead|underground|radial|double-?circuit|single-?circuit|transmission|\d+(?:\.\d+)?\s*kV)\s+){1,4}(?:line|spur|tie-?line|gen-?tie)s?(?:\s+additions?)?\b(?!\s+(?:from|to)\s+(?:the\s+)?(?:[A-Z0-9][\w'-]*\s+){0,3}(?:substation|switchyard|station)\b)/i.test(
      after,
    ) ||
    /\(\s*[\d,]+(?:\.\d+)?\s*(?:km|mi|miles?|kilometers?)\s+(?:plus|\+)\s*$/i.test(before) ||
    /^\s*(?:plus|\+)\s+[\d,]+(?:\.\d+)?\s*(?:km|mi|miles?|kilometers?)\s*\)\s*of\s+(?:new\s+)?(?:\d+(?:\.\d+)?\s*kV\s+)?(?:transmission\s+)?lines?\b/i.test(
      after,
    )
  ) {
    return 'other';
  }
  const markers: Array<[DistanceRole, RegExp]> = [
    ['substation', /\b(?:substations?|switchyards?|interconnection(?!\s+traffic)(?:\s+(?:point|distance))?|electrical\s+interconnect|grid\s+access|nearest\s+sub)\b/gi],
    ['reactor', /\b(?:reactors?|nuclear\s+(?:(?:power|generating)\s+)?(?:plants?|stations?|facilit(?:y|ies)|proximity|distance))\b/gi],
    ['other', /\b(?:fiber|fibre|colocation|internet\s+exchange|IX|peering|pipelines?|dams?|reservoirs?|airports?|airfields?|protected\s+areas?|transmission\s+(?:lines?|corridors?|polylines?)|\d+(?:\.\d+)?\s*kV\s+(?:transmission\s+)?(?:lines?|circuits?|corridors?|polylines?)(?=\s+(?:(?:located|situated|roughly|approximately|about|~)\s+)*\d))\b/gi],
  ];
  const local = text.slice(start, end);
  const at = index - start;
  const candidates = markers.flatMap(([role, pattern]) => [...local.matchAll(pattern)].map(m => ({
    role,
    distance: Math.max(0, m.index! > at ? m.index! - at - length : at - m.index! - m[0].length),
  })));
  candidates.sort((a, b) => a.distance - b.distance);
  if (!candidates.length || candidates[0]!.distance > 130) return 'unknown';
  if (candidates[1] && candidates[1].distance === candidates[0]!.distance && candidates[1].role !== candidates[0]!.role) return 'unknown';
  return candidates[0]!.role;
}
